import React, { useState, useEffect } from "react";
import { Ship, Truck, Plane, ArrowRight, CheckCircle, Shield, Globe, Clock } from "lucide-react";
import Navbar from "../layout/Navbar";
import Footer from "../layout/Footer";
import { Link } from "react-router-dom";

const services = [
    {
        icon: Ship,
        title: "Sea Freight",
        tagline: "Ocean Cargo Solutions",
        description: "Cost-effective ocean freight for bulk cargo across 50+ global ports with full documentation support.",
        features: ["FCL & LCL Shipments", "Port-to-Port Handling", "Custom Documentation", "Real-Time Tracking"],
        num: "01",
    },
    {
        icon: Truck,
        title: "Land Transport",
        tagline: "Pan-India Road Logistics",
        description: "GPS-tracked domestic & cross-border road transport with door-to-door delivery across India.",
        features: ["GPS-Tracked Vehicles", "Door-to-Door Delivery", "Cross-Border Clearance", "24/7 Support"],
        num: "02",
    },
    {
        icon: Plane,
        title: "Air Cargo",
        tagline: "Swift Global Air Freight",
        description: "IATA-certified air freight for urgent & high-value shipments with priority customs clearance.",
        features: ["IATA Certified", "Express Delivery", "Priority Customs", "Worldwide Network"],
        num: "03",
    },
];

const whyUs = [
    { icon: Shield, label: "APEDA Certified" },
    { icon: Globe, label: "10+ Countries" },
    { icon: Clock, label: "24/7 Support" },
    { icon: CheckCircle, label: "IEC Registered" },
];

const ServiceDetails = () => {
    const [heroLoaded, setHeroLoaded] = useState(false);
    const [visible, setVisible] = useState({});

    useEffect(() => {
        setTimeout(() => setHeroLoaded(true), 80);
        const observer = new IntersectionObserver(
            (entries) => entries.forEach((e) => { if (e.isIntersecting) setVisible((p) => ({ ...p, [e.target.id]: true })); }),
            { threshold: 0.15 }
        );
        document.querySelectorAll(".obs").forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    return (
        <>
            <Navbar />
            <div className="min-h-screen bg-white">

                {/* ── HERO ── */}
                <section className="relative bg-gradient-to-br from-green-900 via-emerald-900 to-teal-900 text-white overflow-hidden pt-28 pb-32 text-center">
                    <div
                        className="relative max-w-3xl mx-auto px-6"
                        style={{ opacity: heroLoaded ? 1 : 0, transform: heroLoaded ? "translateY(0)" : "translateY(24px)", transition: "all 0.8s ease" }}
                    >
                        <span className="inline-block text-xs font-bold uppercase tracking-widest text-green-300 bg-green-300/10 border border-green-300/25 px-4 py-2 rounded-full mb-5">
                            Nurani Enterprise · Logistics
                        </span>
                        <h1 className="text-5xl md:text-6xl font-black mb-4 leading-tight">
                            Our <span className="bg-gradient-to-r from-green-300 to-emerald-300 bg-clip-text text-transparent">Services</span>
                        </h1>
                        <p className="text-green-100 text-lg max-w-xl mx-auto leading-relaxed">
                            Sea, Land & Air — reliable logistics solutions for your global trade needs.
                        </p>
                    </div>

                    <div className="absolute bottom-0 left-0 right-0">
                        <svg viewBox="0 0 1440 60" fill="none"><path d="M0 60L720 20L1440 60V60H0Z" fill="white" /></svg>
                    </div>
                </section>

                {/* ── SERVICE CARDS ── */}
                <section className="py-20 bg-white">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6 space-y-10">
                        {services.map((s, i) => {
                            const Icon = s.icon;
                            const isEven = i % 2 === 0;
                            return (
                                <div
                                    key={i}
                                    id={`svc-${i}`}
                                    className="obs rounded-3xl overflow-hidden shadow-lg border border-green-100 flex flex-col hover:shadow-2xl transition-shadow duration-300"
                                    style={{
                                        opacity: visible[`svc-${i}`] ? 1 : 0,
                                        transform: visible[`svc-${i}`] ? "translateY(0)" : "translateY(36px)",
                                        transition: `all 0.6s ease ${i * 0.12}s`,
                                        flexDirection: isEven ? "row" : "row-reverse",
                                    }}
                                >
                                    {/* Icon Panel */}
                                    <div className="lg:w-2/5 bg-gradient-to-br from-green-700 to-emerald-900 p-10 flex flex-col justify-center items-center text-center relative overflow-hidden">
                                        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.5) 1px, transparent 1px)", backgroundSize: "20px 20px" }} />
                                        <span className="absolute top-4 right-5 text-white/10 font-black text-8xl select-none">{s.num}</span>
                                        <div className="relative z-10">
                                            <div className="w-20 h-20 rounded-2xl bg-white/15 border border-white/25 flex items-center justify-center mb-5 mx-auto">
                                                <Icon className="w-10 h-10 text-white" />
                                            </div>
                                            <p className="text-green-200 text-xs font-bold uppercase tracking-widest">{s.tagline}</p>
                                        </div>
                                    </div>

                                    {/* Content Panel */}
                                    <div className="lg:w-3/5 p-10 flex flex-col justify-center bg-white">
                                        <h3 className="text-2xl font-black text-gray-900 mb-2">{s.title}</h3>
                                        <p className="text-gray-500 text-sm leading-relaxed mb-6">{s.description}</p>
                                        <div className="grid grid-cols-2 gap-2.5 mb-7">
                                            {s.features.map((f, j) => (
                                                <div key={j} className="flex items-center gap-2">
                                                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                                                    <span className="text-sm text-gray-700 font-medium">{f}</span>
                                                </div>
                                            ))}
                                        </div>
                                        <button className="inline-flex items-center gap-2 text-green-700 font-bold text-sm hover:gap-3 transition-all duration-300 group">
                                            Get a Quote <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* ── WHY US ── */}
                <section className="py-16 bg-gradient-to-br from-green-50 to-emerald-50">
                    <div className="max-w-4xl mx-auto px-6 text-center">
                        <h2 className="text-3xl font-black text-gray-900 mb-2">Why <span className="text-green-600">Nurani Enterprise?</span></h2>
                        <p className="text-gray-500 text-sm mb-10">25+ years of trusted global trade expertise</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                            {whyUs.map((w, i) => {
                                const Icon = w.icon;
                                return (
                                    <div key={i} className="bg-white rounded-2xl p-6 shadow-md border border-green-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col items-center gap-3">
                                        <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                                            <Icon className="w-6 h-6 text-green-700" />
                                        </div>
                                        <p className="font-bold text-gray-800 text-sm text-center">{w.label}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* ── CTA ── */}
                <section className="py-20 bg-gradient-to-r from-green-700 to-teal-800 text-white text-center relative overflow-hidden">
                    <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.4) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
                    <div className="relative max-w-2xl mx-auto px-6">
                        <h2 className="text-4xl font-black mb-4">Ready to Ship?</h2>
                        <p className="text-green-100 mb-8 leading-relaxed">Partner with us for reliable, efficient international logistics solutions.</p>
                        <Link to="/contact">
                            <button className="bg-white text-green-800 px-10 py-4 rounded-full font-black hover:bg-green-50 hover:scale-105 transition-all duration-300 shadow-xl">
                                Contact Us Today
                            </button>
                        </Link>
                    </div>
                </section>

            </div>
            <Footer />
        </>
    );
};

export default ServiceDetails;